<template>
	<div class="chat">
		<div class="messages-wrap" ref="messages">
			<div class="messages">
				<div class="message-row" v-if="!noMoreLoad">
					<uiButton
						color="grey"
						@click="loadMore"
						:loading="loadingMore"
						>Загрузить ещё</uiButton
					>
				</div>

				<template v-for="(m, index) in messagesWithTicket">
					<p
						:key="'title' + m.id"
						v-if="
							index === 0 ||
							!isDateSameDay(
								messagesWithTicket.at(index - 1).createdAt,
								m.createdAt
							)
						"
						class="bg-white font-semibold my-0 text-xs text-center py-2 top-0 z-1 sticky"
					>
						<span class="opacity-30">{{
							formatCreatedAtDay(m.createdAt)
						}}</span>
					</p>

					<div
						:key="'message' + m.id"
						class="message-row group"
						:class="{
							user: isUser(m.from),
							support: isSupport(m.from),
						}"
						@click.self="onMessageRowClick($event, m)"
					>
						<div class="ticket-start" v-if="m.ticket">
							Начало тикета
						</div>

						<!--
							Which pet the owner aimed this message at, picked on
							the chip in the app's composer. Every client message
							carries it (that's the attribution record), but it is
							only shown where it says something — see
							`showPetHint` below. Nominative, not «про Барсика»:
							names are stored nominative and the accusative would
							be wrong for half of them.
						-->
						<div class="pet-hint" v-if="m.showPetHint">
							Питомец: {{ m.pet.name }}
						</div>

						<div class="flex items-center">
							<div
								v-if="isUser(m.from)"
								class="flex ml-auto space-x-1"
							>
								<div
									class="opacity-0 pr-2 transition-all text-red-500 inline-flex group-hover:opacity-100"
									@click="deleteMessage(m.id)"
								>
									<Icon icon="ph:trash" height="16" />
								</div>

								<div
									v-if="m.kind === 'text'"
									class="opacity-0 pr-2 transition-all text-gray-500 inline-flex group-hover:opacity-100"
									@click="editMessage(m)"
								>
									<Icon icon="ph:pencil" height="16" />
								</div>
							</div>

							<template v-if="m.attachment">
								<div
									v-if="m.attachment.kind === 'photo'"
									class="photo"
								>
									<uiImage :url="m.attachment.url" />
								</div>

								<div
									v-if="m.attachment.kind === 'pdf'"
									class="photo"
								>
									<uiPdf :url="m.attachment.url" />
								</div>

								<div
									v-if="m.attachment.kind === 'video'"
									class="photo"
								>
									<uiVideo
										:preview="m.attachment.preview"
										:url="m.attachment.url"
										fit="contain"
										class="h-25 w-25"
										h="300"
									/>
								</div>

								<PetEvent
									v-if="m.attachment.kind === 'document'"
									:event="getPetEvent(m.attachment)"
								/>
							</template>

							<div v-else class="message">
								{{ m.text }}
							</div>
						</div>

						<div class="info">
							<span
								class="flex space-x-1 items-center"
								:class="{
									'justify-end': isUser(m.from),
								}"
							>
								<span>{{
									messageInfo(m.from, m.fromName, m.createdAt)
								}}</span>
								<Icon
									v-if="isUser(m.from)"
									:icon="
										index > lastReadMessageIndex
											? 'bi:check'
											: 'bi:check-all'
									"
									width="16"
								/>
							</span>
						</div>
					</div>
				</template>

				<div
					v-if="typingUsers.includes(client.id)"
					class="message-row group"
				>
					<div class="flex items-center">
						<div class="message">
							<Icon
								icon="svg-spinners:3-dots-scale"
								width="16"
								class="text-gray-400"
							/>
						</div>
					</div>
				</div>

				<div class="message-row">
					<div class="ticket-start" v-if="!ticket">
						<template v-if="messages.length">Конец тикета</template>
						<template v-else>Пока никто не писал</template>
					</div>
				</div>
			</div>
		</div>

		<div class="row-controller"></div>

		<div v-if="edittingMessage != null" class="flex p-2.5 items-start">
			<Icon icon="ph:pencil-simple" width="20" class="text-[#6266ea]" />

			<div
				class="border-solid border-l-2 border-[#6266ea] border-0 flex-1 mx-2 text-xs px-2"
			>
				<p class="font-bold flex-1 m-0 text-[#6266ea]">
					Изменить сообщение
				</p>
				<p class="flex-1 m-0">{{ edittingMessage.text }}</p>
			</div>

			<div
				class="cursor-pointer flex text-gray-500"
				@click="closeEdittingMessage"
			>
				<Icon icon="ph:x-circle" width="24" />
			</div>
		</div>

		<div class="send" v-if="sendEnabled">
			<textarea
				class="input"
				v-model="message"
				placeholder="Новое сообщение"
				rows="1"
				ref="input"
				@keydown.shift.enter="onInputEnter"
				@keyup.shift.enter="sendMessage"
			/>
			<div
				class="flex send-button items-center justify-center"
				:class="{ disabled: !canSend }"
				@click="sendMessage"
			>
				<Icon
					v-if="edittingMessage != null"
					icon="ph:check"
					width="20"
				/>
				<i v-else class="lni lni-telegram-original" />
			</div>
		</div>
	</div>
</template>

<script>
import autosize from 'autosize'
import { API } from '@/config'
import { format, isSameDay } from 'date-fns'
import { confirm } from '@/utils/events'
import PetEvent from '@/components/PetEvent'

const YEAR = new Date().getFullYear()

export default {
	name: 'ChatView',
	components: {
		PetEvent,
	},
	props: {
		id: String,
		assigned: Boolean,
		assignedToMe: Boolean,
	},
	data() {
		return {
			loading: false,
			loadingMore: false,
			messages: [],
			message: '',
			noMoreLoad: false,
			edittingMessage: null,
			isTyping: false,
			typingTimeout: null,
		}
	},
	computed: {
		chat() {
			return this.$store.getters.chats.find(c => c.id === this.id)
		},
		me() {
			return this.$store.getters.user
		},
		client() {
			return this.chat.client
		},
		sendEnabled() {
			if (this.assigned && !this.assignedToMe) return false
			if (!this.assigned) return true
			return true
		},
		canSend() {
			if (this.edittingMessage?.text === this.message) return false
			return this.message.length > 0
		},
		ticket() {
			return this.$store.getters.tickets.find(
				t => t.id === this.chat.ticket
			)
		},
		messagesWithTicket() {
			const ticketStartId = this.ticket?.start?.id
			// Only the client's messages carry a pet, and only the CHANGES are
			// worth a line: show it on the first one in the loaded thread and
			// every time the subject switches. A single-pet chat therefore
			// states it once at the top instead of on every message, while a
			// multi-pet thread marks exactly where the owner changed subject.
			let lastPetId
			return this.messages.map(m => {
				if (ticketStartId && m.id === ticketStartId) m.ticket = true

				if (this.isUser(m.from) || this.isSupport(m.from)) {
					m.showPetHint = false
					return m
				}
				const petId = m.pet?.id ?? null
				m.showPetHint = !!m.pet?.name && petId !== lastPetId
				lastPetId = petId
				return m
			})
		},
		typingUsers() {
			return this.$store.getters.typingUsers
		},
		lastReadMessageIndex() {
			const lastReadMessageId = this.chat.readBy?.[this.client.id]
			if (lastReadMessageId == null) return this.messagesWithTicket.length

			const index = this.messagesWithTicket.findIndex(
				m => m.id === lastReadMessageId
			)

			return index
		},
	},
	watch: {
		id(nv) {
			if (nv) this.loadMessages()
		},
		async message(val) {
			if (this.edittingMessage || !val) return

			if (this.typingTimeout) {
				clearTimeout(this.typingTimeout)
				this.typingTimeout = null
			} else {
				await this.startTyping()
			}

			this.typingTimeout = setTimeout(async () => {
				await this.stopTyping()
			}, 1000)
		},
	},
	mounted() {
		this.loadMessages()
		autosize(this.$refs.input)
		this.$events.$on('addChatMessage', this.onNewMessage)
		this.$events.$on('startTyping', this.handleUserStartTyping)
		this.$events.$on('chatDeleteMessage', this.handleUserDeleteMessage)

		this.checkForUnreadMessages()
	},
	destroyed() {
		this.$events.$off('addChatMessage', this.onNewMessage)
		this.$events.$off('startTyping', this.handleUserStartTyping)
		this.$events.$off('chatDeleteMessage', this.handleUserDeleteMessage)
	},
	methods: {
		loadMessages(scroll = true) {
			if (!this.id || !this.chat) return
			this.messages = this.chat.messages
			if (scroll) this.scrollToBottom()
		},
		isUser(from) {
			return this.me.id === from
		},
		isSupport(from) {
			return this.me.id !== from && this.chat.client.id !== from
		},
		formatCreatedAtDay(date) {
			const dateObj = new Date(date)
			let form = 'dd MMMM'

			if (dateObj.getFullYear() !== YEAR) {
				form += ' yyyy'
			}

			return format(dateObj, form)
		},
		formatCreatedAt(date) {
			return format(new Date(date), 'HH:mm')
		},
		messageInfo(from, name, date) {
			if (!date) return 'Отправляем'

			const formattedCreatedAt = this.formatCreatedAt(date)

			if (this.me.id === from) return formattedCreatedAt

			return [name, 'в', formattedCreatedAt].join(' ')
		},
		isDateSameDay(start, end) {
			return isSameDay(new Date(start), new Date(end))
		},
		onInputEnter(e) {
			e.preventDefault()
		},
		async sendMessage() {
			if (!this.canSend) return

			if (this.edittingMessage != null) {
				return await this.updateMessage()
			}

			let tempId = 'new-' + Date.now()
			let message = {
				id: tempId,
				from: this.me.id,
				fromName: this.me.name,
				text: this.message,
			}

			this.messages.push(message)
			this.message = ''
			this.scrollToBottom()

			this.$nextTick(() => autosize.update(this.$refs.input))

			let { data } = await this.$http.post(API.CHATS.send, {
				chat: this.id,
				message: message,
			})
			if (!data || data.err) {
				this.message = message.text
				this.$notify(data.err)
				return
			}

			this.$store.commit('updateChatMessages', {
				chat: this.id,
				message: data,
			})
			this.updateMessageById(tempId, data)
		},

		async updateMessage() {
			const id = this.edittingMessage.id
			let { data } = await this.$http.patch(
				API.CHATS.updateMessage + id,
				{
					text: this.message,
				}
			)
			if (!data || data.err) {
				this.$notify(data.err)
				return
			}

			const msg = this.messages.find(m => m.id === id)
			msg.text = data.text
			msg.updatedAt = data.updatedAt

			this.closeEdittingMessage()
		},

		async deleteMessage(id) {
			if (this.edittingMessage != null) {
				this.closeEdittingMessage()
			}

			const confirmed = await confirm('Точно удаляем?')
			if (!confirmed) return

			const { data } = await this.$http.delete(
				API.CHATS.deleteMessage + id
			)
			if (!data || data.err) {
				this.$notify(data.err)
				return
			}

			this.updateMessageById(id)

			this.$store.commit('setChatMessages', {
				chat: this.id,
				messages: this.messages,
			})

			this.$store.commit('updateInArray', {
				path: 'chats',
				data: {
					...this.chat,
					preview: this.messages.at(-1)?.text ?? 'Новое сообщение',
				},
			})
		},

		async editMessage(msg) {
			this.edittingMessage = msg
			this.message = msg.text
		},
		closeEdittingMessage() {
			this.edittingMessage = null
			this.message = ''
		},
		updateMessageById(id, message) {
			let index = this.messages.findIndex(m => m.id === id)

			if (message) {
				this.messages.splice(index, 1, message)
			} else {
				this.messages.splice(index, 1)
			}
		},
		async onNewMessage(e) {
			if (e.chat !== this.id) return
			this.messages.push(e.message)
			this.scrollToBottom()
			await this.markMessageAsRead(e.message.id)
		},
		async loadMore() {
			if (this.loadingMore) return
			this.loadingMore = true

			let payload = {
				chat: this.chat.id,
				id: this.messages[0].id,
			}
			let { data } = await this.$http.post(API.CHATS.more, payload)
			if (!data || data.err) {
				this.$notify(data.err)
				return (this.loadingMore = false)
			}

			let oldH = this.$refs.messages.scrollHeight

			this.messages = data.messages.concat(this.messages)
			this.$store.commit('updateChatMessages', {
				chat: this.id,
				messages: data.messages,
			})

			this.noMoreLoad = data.noMore
			await this.$nextTick()

			this.$refs.messages.scrollTop =
				this.$refs.messages.scrollHeight - oldH
			this.loadingMore = false
		},
		async scrollToBottom() {
			await this.$nextTick()
			this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
		},
		openPhoto(m) {
			this.$events.$emit('imageViewer', [m.attachment.url])
		},
		onMessageRowClick(e, message) {
			if (!e.target) return
			let bounds = e.target.getBoundingClientRect()
			console.log(bounds.top)
			console.log(message)
		},
		getPetEvent(attachment) {
			return {
				kind: 'document',
				title: 'Документ',
				date: null,
				text: '',
				details: {
					document: attachment.documentId,
				},
			}
		},
		async startTyping() {
			await this.$http.post(API.CHATS.startTyping, {
				to: this.client.id,
				name: this.me.name,
			})
		},
		async stopTyping() {
			if (!this.typingTimeout) return

			await this.$http.post(API.CHATS.stopTyping, {
				to: this.client.id,
			})
			this.typingTimeout = null
		},
		async handleUserStartTyping(e) {
			if (e?.from !== this.client.id) return

			await this.$nextTick()

			const el = this.$refs.messages
			const scrollFromBottom =
				el.scrollHeight - el.clientHeight - el.scrollTop
			if (scrollFromBottom < 120) this.scrollToBottom()
		},
		handleUserDeleteMessage(params) {
			const { chat, id } = params
			if (chat !== this.chat.id) return

			const msgIndex = this.messages.findIndex(m => m.id === id)
			this.messages.splice(msgIndex, 1)
		},

		async markMessageAsRead(messageId) {
			await this.$http.post(API.CHATS.markAsRead, {
				chatId: this.id,
				messageId,
			})
		},

		hasUnreadMessage() {
			const lastReadMessageId = this.chat.readBy?.[this.me.id]
			const messages = this.messagesWithTicket.filter(
				m => m.from !== this.me.id
			)

			const readIndex = messages.findIndex(
				m => m.id === lastReadMessageId
			)

			return readIndex + 1 < messages.length
		},

		async checkForUnreadMessages() {
			const has = this.hasUnreadMessage()
			if (!has) return

			const [message] = this.messagesWithTicket
				.filter(m => m.from !== this.me.id)
				.reverse()

			if (!message) return

			await this.markMessageAsRead(message.id)
		},
	},
}
</script>

<style lang="less" scoped>
@import '../assets/app.less';

.chat {
	width: 100%;
	height: 100%;
	background: #fff;
	box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
	border-radius: 16px 16px 30px 30px;
	overflow: hidden;
	position: relative;
	display: flex;
	flex-direction: column;
}

.messages-wrap {
	width: 100%;
	height: 100%;
	overflow-y: auto;
	flex: 1 1 auto;
}

.messages {
	padding: 10px 0;
	padding-bottom: 0;
}

.message-row {
	padding: 5px 20px;
	// margin-bottom: 10px;

	&:hover {
		background: rgba(0, 0, 0, 0.03);
		cursor: pointer;
	}

	.message {
		padding: 8px 14px;
		background: @grey;
		width: fit-content;
		max-width: 80%;
		border-radius: 16px;
		font-size: 15px;

		white-space: pre-line;
		word-wrap: break-word;
		overflow-wrap: break-word;
		hyphens: auto;
	}

	.info {
		font-size: 12px;
		opacity: 0.3;
		margin-top: 3px;
	}

	.pet-hint {
		font-size: 12px;
		opacity: 0.4;
		margin-bottom: 3px;
	}

	&.user {
		.message {
			background: @accent;
			color: #fff;
		}

		.info {
			text-align: right;
		}
	}

	&.support {
		.message {
			background: @yellow;
			color: #000;
			margin-left: auto;
		}

		.info {
			margin-left: auto;
			text-align: right;
		}
	}

	.ticket-start {
		text-align: center;
		font-size: 13px;
		opacity: 0.3;
		margin: 10px 0;
	}
}

.send {
	width: calc(100% - 20px);
	margin: 10px;
	background: @grey;
	border-radius: 25px;
	display: flex;
	align-items: flex-end;
	flex-shrink: 0;

	.input {
		flex-grow: 1;
		margin: 0;
		padding: 0;
		border: 0;
		background: transparent;
		padding: 16px;
		outline: none;
		resize: none;
		max-height: 200px;
		font-size: 15px;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
			Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
	}

	.send-button {
		width: 40px;
		height: 40px;
		background: @accent;
		border-radius: 20px;
		flex-shrink: 0;
		margin: 4px;
		color: #fff;
		font-size: 20px;
		cursor: pointer;
		transition: opacity 250ms ease;

		&.disabled {
			opacity: 0.3;
			pointer-events: none;
			cursor: default;
		}

		&:active {
			transform: scale(0.98);
		}
	}
}
</style>
